import { useEffect, useState, useMemo } from 'react'
import { storage } from '~/utilities/storageUitls'
import { useLocalStorage } from '@uidotdev/usehooks'
import {
  LoginEmailResponse,
  useEmailLoginMutation
} from '~/graphql/hooks/useEmailLoginMutation'
import { useNavigate } from 'react-router-dom'
import Mixpanel from '~/utilities/mixpanel'
import { toast } from 'sonner'
import Loading from '~/components/ui/Loading'

interface OktaTokenStorage {
  idToken: {
    idToken: string
  }
}

/**
 * Type guard to check if the storage value contains a valid Okta idToken
 */
const hasIdToken = (oktaTokenStorage: unknown): oktaTokenStorage is OktaTokenStorage => {
  if (typeof oktaTokenStorage !== 'object' || oktaTokenStorage === null) {
    return false
  }
  if (typeof oktaTokenStorage === 'string') {
    return false
  }
  return (
    'idToken' in oktaTokenStorage &&
    typeof oktaTokenStorage.idToken === 'object' &&
    oktaTokenStorage.idToken !== null &&
    'idToken' in oktaTokenStorage.idToken &&
    typeof oktaTokenStorage.idToken.idToken === 'string'
  )
}

/**
 * Extracts the idToken from Okta token storage safely
 */
const extractIdToken = (oktaTokenStorage: unknown): string | null => {
  if (!hasIdToken(oktaTokenStorage)) {
    return null
  }
  return oktaTokenStorage.idToken.idToken
}

export default function OktaLogin() {
  const navigate = useNavigate()
  const [, setToken] = useLocalStorage('access_token', '')
  const [hasAttemptedLogin, setHasAttemptedLogin] = useState(false)

  const { emailLogin, loading: emailLoginIsLoading } = useEmailLoginMutation({
    onCompleted(data: LoginEmailResponse) {
      if (data.emailLogin?.success && data.emailLogin.data?.token) {
        setToken(data.emailLogin.data.token)
        Mixpanel.identify({
          id: data.emailLogin.data.user.id.toString(),
          data: {
            email: data.emailLogin.data.user.email,
            framework: 'react'
          }
        })
        navigate('/')
        return
      }

      if (data.emailLogin?.success) {
        navigate('/?login=success')
        return
      }

      if (data.emailLogin?.error) {
        toast.error(data.emailLogin.error)
        setHasAttemptedLogin(false)
      }
    },
    onError(error) {
      console.error('Okta login error:', error)
      toast.error('Failed to authenticate with Okta. Please try again.')
      setHasAttemptedLogin(false)
    }
  })

  const oktaTokenStorage = useMemo(() => {
    try {
      return storage.getJSON('okta-token-storage')
    } catch (error) {
      console.error('Failed to parse Okta token storage:', error)
      return null
    }
  }, [])

  const idToken = useMemo(() => extractIdToken(oktaTokenStorage), [oktaTokenStorage])
  const subdomain = useMemo(() => storage.get('okta-auth-subdomain'), [])

  useEffect(() => {
    if (!idToken) return
    if (emailLoginIsLoading) return
    if (hasAttemptedLogin) return

    if (!subdomain) {
      toast.error('Missing Okta subdomain. Please try logging in again.')
      return
    }

    setHasAttemptedLogin(true)

    void emailLogin({
      email: '',
      credentials: idToken,
      provider: 'okta',
      subdomain,
      inviteToken: localStorage.getItem('invite_token') ?? null
    })
  }, [idToken, emailLogin, emailLoginIsLoading, hasAttemptedLogin, subdomain])

  if (emailLoginIsLoading || (idToken && !hasAttemptedLogin)) {
    return <Loading label="Authenticating with Okta..." />
  }

  if (!idToken) {
    return (
      <main className="w-full min-h-screen flex items-center justify-center p-4">
        <div className="text-center space-y-2">
          <p className="text-sm">
            <span className="font-bold">Error |</span> No Okta token authentication found
          </p>
          <p className="text-xs text-muted-foreground">
            Please try logging in again through the Okta login page.
          </p>
        </div>
      </main>
    )
  }

  return (
    <main className="w-full min-h-screen flex items-center justify-center p-4">
      <Loading label="Processing authentication..." />
    </main>
  )
}
